<template>
    <div :class="wrapperClassesProp" @click="handleClick" @dragover.prevent="handleDragOver" @drop.prevent="handleDrop">
        <form ref="dropzone" :action="uploadUrl" class="dropzone" enctype="multipart/form-data" method="POST">
            <div v-show="showMessage" class="dz-message">
                <i class="inticon-scan" :class="{'mb-3': title}"/>
                <h3 v-if="title" class="mb-1">
                    {{ title }}
                </h3>
                <div v-if="showHelpText" class="dz-help-text">
                    <p class="mb-0">
                        {{ $t('drag file or click' + ($root.translationKeySuffix ? $root.translationKeySuffix : '')) }}
                    </p>
                </div>
            </div>
            <slot/>
            <div class="fallback">
                <input ref="fallbackFileInput" :accept="acceptedMimeTypes" :multiple="multipleUpload"
                    :name="fileInputName" type="file" @change="handleFileInputChanged">
            </div>
        </form>
        <div v-show="blockUpload"
            class="block-dropzone"
            @click.stop=""/>
        <div v-if="deactivated" class="btn btn-secondary btn-deactivated">
            nicht aktiviert
        </div>
    </div>
</template>

<script>
    import Dropzone from 'dropzone/dist/dropzone';
    import '@scss/components/base-file-upload.scss';
    import Container from '@services/Container';

    Dropzone.autoDiscover = false;
    const UPLOAD_PRECONDITION_FAILED = 'file rejected by beforeFileAdded';

    export default {
        props: {
            additionalParams: {
                type: Object,
                default() {
                    return {}
                },
            },
            acceptedMimeTypes: {
                type: String,
                default: '',
            },
            autoProcessQueue: {
                type: Boolean,
                default: true,
            },
            blockUpload: {
                type: Boolean,
                default: false,
            },
            deactivated: {
                type: Boolean,
                default: false,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            fileInputName: {
                type: String,
                default: 'file',
            },
            maxFiles: {
                type: Number,
                default: null,
            },
            multipleUpload: {
                type: Boolean,
                default: false,
            },
            onTransform: {
                type: [Function, null],
                default: null,
            },
            title: {
                type: String,
                required: false,
                default: null,
            },
            uploadErrorMessage: {
                type: String,
                default: '',
            },
            uploadUrl: {
                type: String,
                required: true,
            },
            showMessage: {
                type: Boolean,
                default: true,
            },
            onlyAllowDragAndDrop: {
                type: Boolean,
                default: false,
            },
            wrapperClasses: {
                type: Array,
                default: () => ['dropzone-wrapper', 'text-center'],
            },
            showHelpText: {
                type: Boolean,
                default: true,
            },
            // Optional callback – return false to reject the file.
            // Signature: (file) => boolean | Promise<boolean>
            beforeFileAdded: {
                type: Function,
                default: null,
            },
        },
        emits: [
            'after-files-added',
            'file-drop',
            'file-added',
            'file-removed',
            'file-upload-progress',
            'file-upload-complete',
            'upload-complete',
            'upload-error',
            'before-upload',
            'total-upload-progress',
            'upload-deactivated',
            'upload-disabled',
            'upload-reset',
        ],
        data() {
            return {
                numberOfAddedFiles: 0,
                numberOfSuccessfullyUploadedFiles: 0,
                successfullyUploadedFiles: [],
            }
        },
        computed: {
            dropzoneOptions() {
                const options = {
                    // fallbackFileInput is used for clicking
                    clickable: false,
                    maxFiles: this.maxFiles,
                    paramName: this.fileInputName,
                    autoProcessQueue: this.autoProcessQueue,
                    // Default is 2, but api can not handle parallel requests. This will end into deadlock error
                    parallelUploads: 1,
                    params: () => {
                        return this.additionalParams;
                    },
                    accept: (file, done) => {
                        if (this.uploadDisabled) {
                            this.$emit('upload-disabled');
                            file.preconditionFailed = true;
                            done('upload disabled');
                            return;
                        }

                        if (this.beforeFileAdded) {
                            try {
                                const permitted = this.beforeFileAdded(file);
                                if (!permitted) {
                                    file.preconditionFailed = true;
                                    return done(UPLOAD_PRECONDITION_FAILED);
                                }
                            } catch (e) {
                                console.error('beforeFileAdded threw', e);
                                return done('file rejected');
                            }
                        }

                        done();
                    },

                    addedfile: (file) => {
                        // This is called before accept
                        this.numberOfAddedFiles++;
                        this.$emit('file-added', file);
                    },
                    addedfiles: (files) => {
                        this.$emit('after-files-added', files);
                    },
                    removedfile: (file) => {
                        if (file.preconditionFailed) {
                            return
                        }
                        this.numberOfAddedFiles--;
                        this.$emit('file-removed', file);
                    },
                    uploadprogress: (file, progress, bytesSent) => {
                        this.$emit('file-upload-progress', file, progress, bytesSent);
                    },
                    totaluploadprogress: (totaluploadProgress, totalBytes, totalBytesSent) => {
                        this.$emit('total-upload-progress', totaluploadProgress, totalBytes, totalBytesSent);
                    },
                    complete: (file) => {
                        this.$emit('file-upload-complete', file);

                        if (file.preconditionFailed) {
                            return;
                        }

                        // 'autoProcessQueue = false' means one must call processQueue() for each file to be uploaded.
                        // But we want to process all files in the queue with a single trigger.
                        if (!this.autoProcessQueue &&
                            this.dropzone.getQueuedFiles().length > 0) {
                            this.dropzone.processQueue();
                            return;
                        }

                        if (this.multipleUpload && !this.autoProcessQueue &&
                            this.dropzone.getQueuedFiles().length === 0 &&
                            this.dropzone.getUploadingFiles().length === 0) {
                            const statistics = this.getStatistics();
                            const data = this.getData();
                            this.reset();

                            if (statistics.numberOfAddedFiles === 0) {
                                return;
                            }

                            this.handleUploadComplete(data, statistics)
                        }
                    },
                    queuecomplete: () => {
                        if (!this.multipleUpload) {
                            return;
                        }

                        let statistics = this.getStatistics();
                        let data = this.getData();

                        this.reset();

                        if (statistics.numberOfSuccessfullyUploadedFiles === 0) {
                            return;
                        }

                        this.handleUploadComplete(data, statistics)
                    },
                    success: (data) => {
                        const response = JSON.parse(data.xhr.response);
                        if (Object.hasOwn(response, 'error')) {
                            if (!this.multipleUpload) {
                                this.reset();
                            }

                            this.displayLegacyUploadErrors(response.messages);
                            this.$emit('upload-error', data, response.error, data.xhr)
                            return;
                        }

                        this.numberOfSuccessfullyUploadedFiles++;
                        this.successfullyUploadedFiles.push(data);

                        if (Object.hasOwn(response, 'warning')) {
                            this.flashMessenger.addWarning(response.warning);
                        }

                        if (this.multipleUpload) {
                            return;
                        }

                        let statistics = this.getStatistics();
                        this.reset();

                        this.handleUploadComplete(data, statistics)
                    },
                    drop: (event) => {
                        this.$emit('file-drop', event);
                    },
                    error: (file, errorMessage, xhr) => {
                        if (file.preconditionFailed) {
                            // Since addedFile is called before accept, we have to decrement the counter here
                            this.numberOfAddedFiles--;
                            this.$emit('upload-error', file, UPLOAD_PRECONDITION_FAILED, null);
                            return;
                        }

                        if (file.accepted === false || file.status === 'error') {
                            this.numberOfAddedFiles--;
                        }

                        if (!this.multipleUpload) {
                            this.reset();
                        }

                        if (file && file.status === 'canceled') {
                            return;
                        }

                        this.displayUploadErrors(errorMessage, xhr);
                        this.$emit('upload-error', file, errorMessage, xhr);
                    },
                    sending: (file, xhr, formData) => {
                        // as this is the only (or one of few) places we do not use the AjaxService to make a call
                        // to the api, we need to set the csrf token here manually.
                        // In the future it might be nice to integrate ajaxService more deeply to append the header
                        // to the xhr instance.
                        const csrfToken = Container.getInstance().getAjaxService().getCsrfToken();
                        if (csrfToken) {
                            xhr.setRequestHeader('X-CSRF-TOKEN', csrfToken);
                        }
                        this.$emit('before-upload', file, xhr, formData)
                    },
                };

                if (this.onTransform) {
                    options.transformFile = this.onTransform;
                }

                return options;
            },
            wrapperClassesProp() {
                if (!this.uploadDisabled) {
                    return this.wrapperClasses;
                }

                // We use concat instead of push to prevent changing the original array in the prop.
                return this.wrapperClasses.concat(['disabled']);
            },
            uploadDisabled() {
                return this.disabled || this.deactivated;
            },
        },
        watch: {
            uploadUrl(newValue) {
                this.dropzone.options.url = newValue;
            },
            disabled(newValue) {
                if (!this.dropzone) {
                    return
                }

                newValue ? this.dropzone.disable() : this.dropzone.enable()
            },
        },
        mounted() {
            this.dropzone = new Dropzone(
                this.$refs.dropzone,
                this.dropzoneOptions
            );

            if (this.uploadDisabled) {
                this.dropzone.disable()
            }
        },
        created() {
            this.flashMessenger = Container.getInstance().getFlashMessenger();
        },
        methods: {
            getDropzoneInstance() {
                return this.dropzone;
            },
            displayLegacyUploadErrors(errorMessages) {
                this.flashMessenger.addLegacyUploadErrorsAsWarning(errorMessages, this.$createElement);
            },
            displayUploadErrors(errorMessage, xhr) {
                if (!xhr || xhr.status !== 422) {
                    if (this.uploadErrorMessage) {
                        this.flashMessenger.addError(this.uploadErrorMessage);
                    }
                    return;
                }

                const response = JSON.parse(errorMessage);
                if (!response) {
                    console.error('Error parsing JSON of validation error response!', errorMessage);
                }
                this.flashMessenger.addErrorsAsWarning(response.errors, this.$createElement);
            },
            getStatistics() {
                return {
                    numberOfAddedFiles: this.numberOfAddedFiles,
                    numberOfSuccessfullyUploadedFiles: this.numberOfSuccessfullyUploadedFiles,
                }
            },
            getData() {
                return {
                    uploadedFiles: this.successfullyUploadedFiles,
                }
            },
            handleClick() {
                if (this.onlyAllowDragAndDrop) {
                    return;
                }

                if (this.deactivated) {
                    this.$emit('upload-deactivated')
                    return;
                }

                if (this.uploadDisabled) {
                    this.$emit('upload-disabled');
                    return;
                }

                if (this.$refs.fallbackFileInput) {
                    this.$refs.fallbackFileInput.click();
                }
            },
            handleDragOver(event) {
                if (this.uploadDisabled) {
                    // Prevent Dropzone and browsers from doing anything
                    event.preventDefault();
                }
            },
            handleDrop() {
                if (this.uploadDisabled) {
                    this.$emit('upload-disabled');
                }
            },
            handleUploadComplete(data, statistics) {
                this.$emit('upload-complete', data, statistics);
            },
            handleFileInputChanged() {
                if (!this.$refs.fallbackFileInput) {
                    return;
                }
                const files = this.$refs.fallbackFileInput.files;
                if (!files.length) {
                    return;
                }
                for (var i = 0; i < files.length; i++) {
                    this.dropzone.addFile(files[i]);
                }
                this.numberOfFiles = files.length;
                // call this, to stay consistent with the dropzone handling for input change
                this.dropzone.emit('addedfiles', files)
            },
            reset() {
                this.dropzone.removeAllFiles(true);

                if (this.$refs.fallbackFileInput) {
                    this.$refs.fallbackFileInput.value = null;
                }

                this.numberOfAddedFiles = 0;
                this.numberOfSuccessfullyUploadedFiles = 0;
                this.successfullyUploadedFiles = [];

                this.$emit('upload-reset');
            },
        },
    }

</script>
